import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, SimpleChanges, ViewChild} from '@angular/core';
import {DomSanitizer} from "@angular/platform-browser";
import {MatChipListbox} from "@angular/material/chips";
import {
  DEFAULT_TOOLS_SECTION_ORDER,
  getLastToolsSection,
  getToolsSectionOrder,
  promoteToolsSection,
  ToolsSectionId,
  TOOLS_SECTION_ORDER_KEY,
} from './browse-tools-mru';

@Component({
  standalone: false,
  selector: 'app-rightbar',
  templateUrl: './rightbar.component.html',
  styleUrls: ['./rightbar.component.scss']
})
export class RightbarComponent implements OnInit, OnChanges {
  @Input() urlList;
  @Input() urlName;
  @Input() count;
  @Input() avgPriceRange;
  @Output() public sidenavItemToggle = new EventEmitter();
  @Input() savedFilters: any[] = [];
  @Input() recentlyViewed: any[] = [];
  @Output() recentViewedClicked = new EventEmitter<any>();

  @Input() recentSearches: any[] = [];
  @Output() filterApplied = new EventEmitter<any>();
  @Output() filterSaved = new EventEmitter<any>();
  @Output() resetFilter = new EventEmitter<any>();
  @Output() deleteFilter = new EventEmitter<any>();
  @Output() recentApplied = new EventEmitter<any>();
  @Output() searchApplied = new EventEmitter<any>();
  @Input() compareItems: any[] = [];
  @Input() popularTags: string[] = [];
  @Output() itemCompared = new EventEmitter<any>();
  @Output() compareRemoved = new EventEmitter<any>();
  @Output() compareStarted = new EventEmitter<void>();
  @ViewChild(MatChipListbox) chipListbox!: MatChipListbox;
  currentFilter: any;

  private readonly SECTION_STATE_KEY = 'browse_tools_sections_v2';
  private readonly SECTION_TOUCHED_KEY = 'browse_tools_sections_touched_v2';
  private readonly defaultSectionOrder = DEFAULT_TOOLS_SECTION_ORDER;
  /** Closed by default; sections with data auto-open unless the user closed them. */
  private readonly defaultSectionState: Record<ToolsSectionId, boolean> = {
    saved: false,
    stats: false,
    searches: false,
    compare: false,
    viewed: false,
  };
  sectionOpen: Record<ToolsSectionId, boolean> = {...this.defaultSectionState};
  sectionOrder: ToolsSectionId[] = [...this.defaultSectionOrder];
  private userTouched: Record<string, boolean> = {};

  removeCompare(item: any) {
    this.compareRemoved.emit(item);
  }

  startCompare() {
    this.promoteSection('compare');
    this.compareStarted.emit();
  }

  applySearch(search: any) {
    this.promoteSection('searches');
    this.searchApplied.emit(search);
  }

  searchTag(tag: string) {
    this.promoteSection('searches');
    this.searchApplied.emit({summary: tag, filters: {keyword: tag}});
  }

  applyFilter(filter: any) {
    filter.selected = !filter.selected;
    this.promoteSection('saved');
    this.filterApplied.emit(filter);
  }

  deleteSavedFilter(filter: any) {
    this.deleteFilter.emit(filter);
  }

  resetFilters() {
    if (this.chipListbox && this.chipListbox._chips) {
      this.chipListbox._chips.forEach(chip => {
        if (chip.selected) {
          chip.deselect();
        }
      });
    }
    this.savedFilters.forEach(filter => filter.selected = false);
    this.resetFilter.emit();
  }

  removeFilter(filter) {
    this.deleteFilter.emit(filter);
  }

  constructor(public sanitizer: DomSanitizer) {
  }

  ngOnInit(): void {
    this.restoreSectionState();
    this.sectionOrder = getToolsSectionOrder();
    this.syncSectionOpenState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['compareItems']) {
      this.openCompareWhenItemsAdded(changes['compareItems']);
    }
    if (changes['savedFilters']
      || changes['recentSearches']
      || changes['recentlyViewed']
      || changes['compareItems']
      || changes['count']
      || changes['avgPriceRange']) {
      this.syncSectionOpenState();
    }
  }

  isSectionOpen(id: ToolsSectionId): boolean {
    return !!this.sectionOpen[id];
  }

  toggleSection(id: ToolsSectionId): void {
    this.sectionOpen[id] = !this.isSectionOpen(id);
    this.userTouched[id] = true;
    if (this.sectionOpen[id]) {
      this.promoteSection(id);
    } else {
      this.persistSectionState();
    }
  }

  /** Move a section to the top of the Tools list (most recently used). */
  promoteSection(id: ToolsSectionId): void {
    this.sectionOrder = promoteToolsSection(id);
    this.persistSectionState();
  }

  /** Force-open Compare only when the user adds an item — not when restoring an existing tray. */
  private openCompareWhenItemsAdded(change: SimpleChange): void {
    if (!change || change.firstChange) {
      return;
    }
    const prevLen = change.previousValue?.length || 0;
    const currLen = change.currentValue?.length || 0;
    if (currLen > prevLen) {
      this.sectionOpen.compare = true;
      this.userTouched.compare = false;
      this.promoteSection('compare');
    }
  }

  private sectionHasContent(id: ToolsSectionId): boolean {
    switch (id) {
      case 'saved':
        return (this.savedFilters?.length || 0) > 0;
      case 'stats':
        return Number(this.count) > 0
          || Number(this.avgPriceRange?.avgPrice) > 0
          || Number(this.avgPriceRange?.maxPrice) > 0;
      case 'searches':
        return (this.recentSearches?.length || 0) > 0;
      case 'compare':
        return (this.compareItems?.length || 0) > 0;
      case 'viewed':
        return (this.recentlyViewed?.length || 0) > 0;
      default:
        return false;
    }
  }

  /** Auto-open the last-used section when it has data; otherwise open any section with content. */
  private syncSectionOpenState(): void {
    const lastUsed = getLastToolsSection();
    const preferLastUsed = !!lastUsed && this.sectionHasContent(lastUsed);
    (Object.keys(this.defaultSectionState) as ToolsSectionId[]).forEach(id => {
      if (this.userTouched[id]) {
        return;
      }
      if (preferLastUsed) {
        this.sectionOpen[id] = id === lastUsed;
        return;
      }
      this.sectionOpen[id] = this.sectionHasContent(id);
    });
  }

  private restoreSectionState(): void {
    try {
      const raw = sessionStorage.getItem(this.SECTION_STATE_KEY);
      const touchedRaw = sessionStorage.getItem(this.SECTION_TOUCHED_KEY);
      if (touchedRaw) {
        const touched = JSON.parse(touchedRaw);
        if (touched && typeof touched === 'object') {
          this.userTouched = touched;
        }
      }
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          this.sectionOpen = {...this.defaultSectionState, ...parsed};
        }
      }
      this.sectionOrder = getToolsSectionOrder();
    } catch {
      this.sectionOrder = getToolsSectionOrder();
    }
  }

  private persistSectionState(): void {
    try {
      sessionStorage.setItem(this.SECTION_STATE_KEY, JSON.stringify(this.sectionOpen));
      sessionStorage.setItem(this.SECTION_TOUCHED_KEY, JSON.stringify(this.userTouched));
      sessionStorage.setItem(TOOLS_SECTION_ORDER_KEY, JSON.stringify(this.sectionOrder));
    } catch {
      // ignore
    }
  }

  buildRouterLink(item: any): string[] {
    const segments = ['/'];

    if (item.profile) {
      segments.push(item.profile);
    }
    if (item.url?.categoryUrl) {
      segments.push(item.url.categoryUrl);
    }
    const itemType = item.url?.itemType;
    if (itemType && itemType !== 'auto') {
      segments.push(itemType);
    }
    if (item.url?.subcategoryUrl) {
      segments.push(item.url.subcategoryUrl);
    }
    if (item.url?.subSubcategoryUrl) {
      segments.push(item.url.subSubcategoryUrl);
    }
    if (item.url?.subSubSubcategoryUrl) {
      segments.push(item.url.subSubSubcategoryUrl);
    }
    if (item.url?.subSubSubSubcategoryUrl) {
      segments.push(item.url.subSubSubSubcategoryUrl);
    }
    if (item.url?.subSubSubSubSubcategoryUrl) {
      segments.push(item.url.subSubSubSubSubcategoryUrl);
    }
    if (item.url?.subSubSubSubSubSubcategoryUrl) {
      segments.push(item.url.subSubSubSubSubSubcategoryUrl);
    }
    if (item.url?.subSubSubSubSubSubSubcategoryUrl) {
      segments.push(item.url.subSubSubSubSubSubSubcategoryUrl);
    }
    if (item.url?.subSubSubSubSubSubSubSubcategoryUrl) {
      segments.push(item.url.subSubSubSubSubSubSubSubcategoryUrl);
    }

    const last = segments[segments.length - 1];
    if (last && last !== '/' && last !== 'all' && !String(last).startsWith('all-')) {
      segments.push('all');
    }
    return segments;
  }


  getImageSrc(item: any): string {
    const lastCategory = this.getLastCategory(item);
    return `assets/images/icons/category-svg-blue/${lastCategory}.svg`;
  }

  getDisplayText(item: any): string {
    return this.getLastCategoryName(item);
  }

  isActive(item: any): boolean {
    const lastUrl = this.getLastCategory(item);
    const lastName = this.getLastCategoryName(item);
    return this.urlName === lastUrl || lastName === 'all ' + this.urlName;
  }

  getLastCategory(item: any): string {
    const categories = [
      item.url.subSubSubSubSubSubSubSubcategoryUrl,
      item.url.subSubSubSubSubSubSubcategoryUrl,
      item.url.subSubSubSubSubSubcategoryUrl,
      item.url.subSubSubSubSubcategoryUrl,
      item.url.subSubSubSubcategoryUrl,
      item.url.subSubSubcategoryUrl,
      item.url.subSubcategoryUrl,
      item.url.subcategoryUrl
    ];
    return categories.find(cat => cat) || '';
  }

  getLastCategoryName(item: any): string {
    const names = [
      item.url.subSubSubSubSubSubSubSubcategory,
      item.url.subSubSubSubSubSubSubcategory,
      item.url.subSubSubSubSubSubcategory,
      item.url.subSubSubSubSubcategory,
      item.url.subSubSubSubcategory,
      item.url.subSubSubcategory,
      item.url.subSubcategory,
      item.url.subcategory
    ];
    return names.find(name => name) || '';
  }

  getSanitizedSvg(svg: string) {
    if (!svg) {
      return '';
    }
    const modifiedSvg = svg.replace(/width="[^"]*"/g, '').replace(/height="[^"]*"/g, '');
    return this.sanitizer.bypassSecurityTrustHtml(modifiedSvg);
  }

  onRecentViewedClick(item: any) {
    this.promoteSection('viewed');
    this.recentViewedClicked.emit(item);
  }

  savedFilterLabel(filter: any): string {
    if (!filter) {
      return 'Saved filter';
    }
    if (filter.summary) {
      return filter.summary;
    }
    if (filter.keyword) {
      return filter.keyword;
    }
    if (filter.location) {
      return filter.location;
    }
    if (filter.subcategory) {
      return filter.subcategory;
    }
    const attrs = filter.attributes || {};
    const first = Object.entries(attrs).find(([, v]) => v !== null && v !== undefined && `${v}`.trim() !== '');
    if (first) {
      return `${first[0]}:${first[1]}`;
    }
    return 'Saved filter';
  }

  attributeEntries(attrs: Record<string, unknown> | null | undefined): Array<{key: string; value: string}> {
    if (!attrs || typeof attrs !== 'object') {
      return [];
    }
    return Object.entries(attrs)
      .filter(([, v]) => v !== null && v !== undefined && `${v}`.trim() !== '')
      .map(([key, value]) => ({key, value: `${value}`}));
  }
}
